import React from 'react'
import { Block, ButtonIcon, Button, Dialog, Input, Text } from '@mote/ui'
import styled from 'styled-components'
import { useAccountUserDomainMutation } from '@mote/api'
import { EditIcon } from '@mote/assets/icons'
import { Content } from '../Dashboard/components'

const CONFIRMATION_KEYWORD = 'CONFIRM'

export const AccountUserDomains = ({ account, onAccountUpdated }) => {
  console.log(account)
  const [dialogData, setDialogData] = React.useState(null)
  const [domain, setDomain] = React.useState('')
  const [domainConfirmation, setDomainConfirmation] = React.useState('')
  const [needsGoogleOverride, setNeedsGoogleOverride] = React.useState(false)
  const { mutate, loading } = useAccountUserDomainMutation(
    dialogData?.domainType
  )
  const [validationError, setValidationError] = React.useState(null)

  function handleDomainConfig(
    intent = 'Add',
    domainNormalizedDescription,
    exampleDomain,
    domainType
  ) {
    setDialogData({
      intent,
      domainNormalizedDescription,
      exampleDomain,
      domainType
    })
    if (intent === 'Edit') {
      setDomain(exampleDomain)
    }
  }

  function closeDialog() {
    setDialogData(null)
    setDomain('')
    setDomainConfirmation('')
    setValidationError('')
    setNeedsGoogleOverride(false)
  }

  function handleDomainInput(e) {
    setDomain(e.target.value)
  }

  function handleDomainConfirmation(e) {
    setDomainConfirmation(e.target.value.toUpperCase())
  }

  async function verifyDomain() {
    setValidationError(null)

    const res = await mutate({
      variables: {
        googleOverride: domainConfirmation === CONFIRMATION_KEYWORD,
        domain
      }
    })

    if (!res.data) return

    if (res.data.domainUpdated) {
      onAccountUpdated()
      closeDialog()
    }

    if (res.data.error) {
      setValidationError(res.data.error)
    }

    if (res.data.needsGoogleOverride !== needsGoogleOverride) {
      setNeedsGoogleOverride(res.data.needsGoogleOverride)
    }
  }

  return (
    <>
      <Content type='section' title='Manage domains' noPadding>
        <StripedBlock maxWidth={550}>
          {account.enableForcedMinorUnlimitedDomain && (
            <Row layout='horizontal' align='center'>
              <Property>Student Unlimited</Property>
              {!account.forcedMinorUnlimitedDomain && (
                <Value>
                  <Button
                    text={'Add domain'}
                    onClick={_ =>
                      handleDomainConfig(
                        'Add',
                        'Student  unlimited',
                        'student.mote.com',
                        'domainUnlimitedForcedMinor'
                      )
                    }
                    variant='link'
                  />
                </Value>
              )}

              {account.forcedMinorUnlimitedDomain && (
                <>
                  <Value>{account.forcedMinorUnlimitedDomain}</Value>
                  <ButtonIcon
                    inverted
                    icon={<EditIcon />}
                    onClick={_ =>
                      handleDomainConfig(
                        'Edit',
                        'Student  unlimited',
                        account.forcedMinorUnlimitedDomain,
                        'domainUnlimitedForcedMinor'
                      )
                    }
                  />
                </>
              )}
            </Row>
          )}

          {account.enableFallbackUnlimitedDomain && (
            <Row layout='horizontal' align='center'>
              <Property>Teacher Unlimited</Property>
              {!account.fallbackUnlimitedDomain && (
                <Value>
                  <Button
                    text='Add domain'
                    onClick={_ =>
                      handleDomainConfig(
                        'Add',
                        'Teacher unlimited',
                        'teacher.mote.com',
                        'domainUnlimited'
                      )
                    }
                    variant='link'
                  />{' '}
                </Value>
              )}

              {account.fallbackUnlimitedDomain && (
                <>
                  <Value>{account.fallbackUnlimitedDomain}</Value>
                  <ButtonIcon
                    inverted
                    onClick={_ =>
                      handleDomainConfig(
                        'Edit',
                        'Teacher  unlimited',
                        account.fallbackUnlimitedDomain,
                        'domainUnlimited'
                      )
                    }
                    icon={<EditIcon />}
                  />
                </>
              )}
            </Row>
          )}
        </StripedBlock>
      </Content>

      {dialogData && (
        <Dialog isOpen onClose={closeDialog} width={350}>
          <Dialog.Heading align='left'>
            {dialogData.intent} domain
          </Dialog.Heading>
          <Dialog.Content>
            <Block gap={12}>
              <Block layout='horizontal' gap={5}>
                <Text tag='p' color='#646F7B' fontWeight={400}>
                  {dialogData.intent} a domain for
                </Text>
                <Text tag='p' fontWeight={500}>
                  {dialogData.domainNormalizedDescription}
                </Text>
              </Block>
              <Input
                onChange={handleDomainInput}
                placeholder={`e.g ${dialogData.exampleDomain}`}
                value={domain}
                invalid={validationError}
              />
              {validationError && (
                <Text tag='small' color='#f44336'>
                  {validationError}
                </Text>
              )}

              {needsGoogleOverride && (
                <Block gap={12}>
                  <Text tag='p' fontWeight={400} color='#646F7B'>
                    Type the word <b>CONFIRM</b> below if you are really sure
                    this is the correct domain:
                  </Text>

                  <Input
                    onChange={handleDomainConfirmation}
                    value={domainConfirmation}
                  />
                </Block>
              )}
            </Block>
          </Dialog.Content>
          <Dialog.ButtonGroup alignment='flex-end' intent='confirmation'>
            <Button
              text='Cancel'
              variant='secondary'
              size='sm'
              onClick={closeDialog}
            />
            <Button
              text={`${dialogData.intent} domain`}
              size='sm'
              loading={loading}
              loadingText={`${dialogData.intent}ing domain`}
              disabled={
                loading ||
                !domain ||
                (domain &&
                  needsGoogleOverride &&
                  domainConfirmation !== CONFIRMATION_KEYWORD)
              }
              onClick={verifyDomain}
            />
          </Dialog.ButtonGroup>
        </Dialog>
      )}
    </>
  )
}

const StripedBlock = styled(Block)`
  & > :nth-of-type(odd) {
    background: #f9f9fb;
  }
`

const Row = styled(Block)`
  padding: 22px 16px;
  border-radius: 4px;
`

const Property = styled.span`
  color: #232323;
  font-size: 15px;
  line-height: 120%;
  font-weight: 500;
  text-transform: capitalize;
  flex: 0 0 240px;

  @media (max-width: 639px) {
    flex: 0 0 130px;
  }
`

const Value = styled.span`
  color: #646f7b;
  font-size: 15px;
  font-weight: 400;
  line-height: 120%;
  text-align: left;
  width: 100%;
`
